import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AddOn, Product, ProductVariant , ChosenOption} from '../api/types';

export interface CartLine {
  /** Stable key for the line: variant plus its chosen extras and note. */
  key: string;
  productId: string;
  productName: string;
  imageUrl: string | null;
  variantId: string;
  variantLabel: string;
  unitPrice: number;
  quantity: number;
  addOns: AddOn[];
  /** Flavours and picks made on this line. */
  choices: ChosenOption[];
  notes: string | null;
  /** Whether this line can carry a loyalty free item. Comes from the category. */
  earnsLoyaltyStamp: boolean;
}

interface CartState {
  lines: CartLine[];
  itemCount: number;
  /** Indicative only — the API reprices everything at checkout. */
  subtotal: number;
  add: (
    product: Product,
    variant: ProductVariant,
    addOns: AddOn[],
    notes: string | null,
    quantity?: number,
    earnsLoyaltyStamp?: boolean,
    /** Flavours and picks. Validated again server-side at checkout. */
    choices?: ChosenOption[],
  ) => void;
  setQuantity: (key: string, quantity: number) => void;
  remove: (key: string) => void;
  clear: () => void;
}

const STORAGE_KEY = 'es.cart';

const CartContext = createContext<CartState | null>(null);

function lineKey(variantId: string, addOns: AddOn[], notes: string | null, choices: ChosenOption[] = []) {
  const extras = addOns
    .map((a) => a.id)
    .sort()
    .join(',');

  // Choices are part of the identity: a Gym Combo with chocolate protein is a
  // different line from one with banana, and merging them would send the
  // kitchen a ticket for two of whichever was added first. Not sorted — four
  // Berry Blasts and one Tropical is a different box from the reverse order
  // only if the counts differ, so the multiset is what matters.
  const picks = choices
    .map((c) => c.choiceId)
    .sort()
    .join(',');

  return `${variantId}|${extras}|${picks}|${notes ?? ''}`;
}

function readStoredCart(): CartLine[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    return raw ? (JSON.parse(raw) as CartLine[]) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(readStoredCart);

  // Survives a refresh and the redirect out to Paystack and back.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  }, [lines]);

  const add = useCallback<CartState['add']>((product, variant, addOns, notes, quantity = 1, earnsLoyaltyStamp = false, choices = []) => {
    const key = lineKey(variant.id, addOns, notes, choices);

    setLines((current) => {
      const existing = current.find((line) => line.key === key);

      if (existing) {
        // Same drink, same size, same extras — bump the quantity instead of
        // stacking a duplicate line.
        return current.map((line) =>
          line.key === key ? { ...line, quantity: line.quantity + quantity } : line,
        );
      }

      return [
        ...current,
        {
          key,
          productId: product.id,
          productName: product.name,
          imageUrl: product.imageUrl,
          variantId: variant.id,
          variantLabel: variant.label,
          unitPrice: variant.price,
          quantity,
          addOns,
          choices,
          notes,
          earnsLoyaltyStamp,
        },
      ];
    });
  }, []);

  const setQuantity = useCallback((key: string, quantity: number) => {
    setLines((current) =>
      quantity <= 0
        ? current.filter((line) => line.key !== key)
        : current.map((line) => (line.key === key ? { ...line, quantity } : line)),
    );
  }, []);

  const remove = useCallback((key: string) => {
    setLines((current) => current.filter((line) => line.key !== key));
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const value = useMemo(() => {
    const subtotal = lines.reduce(
      (total, line) =>
        total + line.quantity * (line.unitPrice + line.addOns.reduce((sum, a) => sum + a.price, 0)),
      0,
    );

    return {
      lines,
      itemCount: lines.reduce((count, line) => count + line.quantity, 0),
      subtotal,
      add,
      setQuantity,
      remove,
      clear,
    };
  }, [lines, add, setQuantity, remove, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error('useCart must be used inside <CartProvider>');
  }

  return context;
}

export function lineTotal(line: CartLine) {
  return line.quantity * (line.unitPrice + line.addOns.reduce((sum, a) => sum + a.price, 0));
}
